import * as React from "react";
import type { PaymentMethodEntry } from "@repo/api-client";
import type { BookingStatus } from "@repo/types";
import { qk } from "../query";
import { useBookingActions } from "./use-booking-actions";

const numeric = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export type DashboardBookingActionBooking = {
  id: string;
  total_amount?: number | string | null;
  deposit_amount?: number | string | null;
  deposit_due_amount?: number | string | null;
  deposit_payment_method?: string | null;
  deposit_payment_methods?: PaymentMethodEntry[] | null;
  note?: string | null;
  depositPlan: {
    suggestedAmount: number;
  };
};

const getSuggestedDepositAmount = (booking: DashboardBookingActionBooking) => {
  if (booking.depositPlan.suggestedAmount > 0) {
    return booking.depositPlan.suggestedAmount;
  }
  const dueAmount = numeric((booking as { deposit_due_amount?: unknown }).deposit_due_amount);
  if (dueAmount > 0) {
    return dueAmount;
  }
  return numeric(booking.deposit_amount);
};

type DashboardBookingActionOptions<TBooking extends DashboardBookingActionBooking> = {
  selectedDate: string;
  onCheckInSuccess?: () => void;
  onCheckInError?: (error: unknown) => void;
  onDepositSuccess?: (result: { lineNotified: boolean }, booking: TBooking) => void;
  onDepositError?: (error: unknown) => void;
  onStatusSuccess?: (status: BookingStatus) => void;
  onStatusError?: (error: unknown) => void;
  onCheckoutSuccess?: () => void;
  onCheckoutError?: (error: unknown) => void;
  onCancelSuccess?: () => void;
  onCancelError?: (error: unknown) => void;
};

export function useDashboardBookingActions<TBooking extends DashboardBookingActionBooking>({
  selectedDate,
  onCheckInSuccess,
  onCheckInError,
  onDepositSuccess,
  onDepositError,
  onStatusSuccess,
  onStatusError,
  onCheckoutSuccess,
  onCheckoutError,
  onCancelSuccess,
  onCancelError,
}: DashboardBookingActionOptions<TBooking>) {
  const {
    checkInMutation,
    confirmDepositMutation: depositMutation,
    updateStatus,
    confirmCheckout,
    cancelBooking,
  } = useBookingActions({
    getInvalidateQueryKeys: () => [
      qk.dashboardBookings(selectedDate),
      qk.dashboardDepositBookings(),
      qk.bookingActivity(),
      ["dashboard-bookings-avail"],
    ],
    onCheckInSuccess,
    onCheckInError,
    onStatusSuccess,
    onStatusError,
    onCheckoutSuccess,
    onCheckoutError,
    onCancelSuccess,
    onCancelError,
    onDepositSuccess: (result) => {
      onDepositSuccess?.(result, depositMutation.variables?.booking as TBooking);
    },
    onDepositError,
  });

  const confirmDepositForBooking = React.useCallback(
    (booking: TBooking) => {
      depositMutation.mutate({
        bookingId: booking.id,
        booking,
        depositAmount: getSuggestedDepositAmount(booking),
        totalAmount: booking.total_amount,
        depositPaymentMethod: booking.deposit_payment_method ?? null,
        depositPaymentMethods: booking.deposit_payment_methods ?? null,
        note: booking.note ?? null,
      });
    },
    [depositMutation]
  );

  return {
    checkInMutation,
    depositMutation,
    confirmDepositForBooking,
    updateStatus,
    confirmCheckout,
    cancelBooking,
  };
}
